import type { LoadHeicConverter } from '../types';

const HEIC_TYPES = new Set(['image/heic', 'image/heif']);

const isHeic = (file: File) => HEIC_TYPES.has(file.type) || /\.hei[cf]$/i.test(file.name);

const toJpegName = (name: string) => `${name.replace(/\.[^./\\]+$/, '')}.jpg`;

/**
 * Converts HEIC/HEIF files to JPEG (most browsers can't render HEIC); other
 * files pass through untouched. The consumer supplies `loadConverter` as a
 * dynamic import so the ~1.5MB codec is code-split and loaded only on a HEIC
 * pick; an import here would be downlevelled to an eager `require` by this
 * package's CommonJS output. Missing converter or failed conversion lands the
 * file in `failed`; a converted JPEG over `maxFileSizeBytes` in `oversized`.
 */
export async function convertHeicFiles(
  files: File[],
  {
    loadConverter,
    maxFileSizeBytes,
  }: { loadConverter?: LoadHeicConverter; maxFileSizeBytes?: number },
): Promise<{ converted: File[]; failed: File[]; oversized: File[] }> {
  const converted: File[] = [];
  const failed: File[] = [];
  const oversized: File[] = [];
  let convert: Awaited<ReturnType<LoadHeicConverter>> | undefined;

  for (const file of files) {
    if (!isHeic(file)) {
      converted.push(file);
      continue;
    }
    if (!loadConverter) {
      failed.push(file);
      continue;
    }
    try {
      convert ??= await loadConverter();
      const out = await convert({ blob: file, toType: 'image/jpeg', quality: 0.9 });
      const blob = Array.isArray(out) ? out[0] : out;
      const jpeg = new File([blob], toJpegName(file.name), { type: 'image/jpeg' });
      if (maxFileSizeBytes != null && jpeg.size > maxFileSizeBytes) {
        oversized.push(jpeg);
      } else {
        converted.push(jpeg);
      }
    } catch {
      failed.push(file);
    }
  }

  return { converted, failed, oversized };
}
